import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { useAuth } from "../contexts/AuthContext";
import { withTimeout } from "../lib/submitQueue";
import { PHOTO_ACCEPT, PDF_ACCEPT } from "../lib/scanToPdf";

// Non-blocking feedback button (Jill 2026-07-17): floats out of the way, opens a
// short structured form, and emails the details (with screenshots) to Jill plus
// a durable copy in the feedback table. Version, page, and identity ride along
// automatically so she stops having to chase those. Videos (Jake Baken
// 2026-08-20) upload to the feedback-videos bucket and email as signed links.

// The menu of portal areas, and the route fragments that belong to each one.
// These live together on purpose: the same list lays out the menu and works out
// which area the pilot is standing in, so the guess can never drift away from
// the choices on offer as the portal grows (Jill 2026-08-30 — the trainee side
// had no entries at all, and the guesser only knew five routes).
const AREA_GROUPS = [
  {
    group: "Pilot",
    areas: [
      { name: "Pilot Slip", paths: ["pilot-slip", "trip-workspace", "batch-slips"] },
      { name: "Transit Brief", paths: ["voyage-plan"] },
      { name: "Expense Report", paths: ["expense-report"] },
      { name: "Dispatch / My Schedule", paths: ["dispatch-calendar", "dispatch-upload", "winter-roster-upload", "pilot-boat-schedule", "calendar-edits"] },
      { name: "Handoff to another pilot", paths: [] },
      { name: "Travel", paths: ["my-travel", "travel-sheet", "reservations-upload"] },
      { name: "Requests", paths: ["pilot-requests"] },
      { name: "Sea Service", paths: ["sea-service"] },
      { name: "Continuing Education", paths: ["continuing-education", "ce-course-form", "ce-observer-form", "ce-audits", "manage-ce-courses"] },
      { name: "Vessel Info", paths: ["vessel-slips", "vessels"] },
    ],
  },
  {
    group: "Training",
    areas: [
      { name: "Training Records", paths: ["training-record", "trainee-trip", "progress-tracker", "training-dashboard"] },
      { name: "Training Opportunities", paths: ["training-opportunities", "trainee-digest", "claa-trainee-queue"] },
      { name: "Grading and reviews", paths: ["tr-grading", "mentor-review", "committee-review", "training-chair", "training-requirements", "trainee-seniority"] },
      { name: "Cargo Training Requests", paths: ["cargo-pool"] },
    ],
  },
  {
    group: "Board",
    areas: [
      { name: "Board Workspace", paths: ["board-workspace", "board-queue", "board-reports", "bod-requests", "bod", "post-to-fleet"] },
      { name: "Ask the Board", paths: ["ask-board"] },
    ],
  },
  {
    group: "Everything else",
    areas: [
      { name: "My Profile", paths: ["pilot-profile", "profile"] },
      { name: "Ask", paths: ["ask-rules"] },
      { name: "Login or offline", paths: ["login", "set-password", "support"] },
      { name: "Something else", paths: [] },
    ],
  },
];

// Longest fragment first, so a specific page beats a shorter fragment that
// happens to sit inside it.
const AREA_ROUTES = AREA_GROUPS
  .flatMap((g) => g.areas.flatMap((a) => a.paths.map((path) => [path, a.name])))
  .sort((a, b) => b[0].length - a[0].length);

const CONNECTIONS = [
  "Ship wifi", "Office / home stable wifi", "Cellular / 5G", "Cellular / LTE",
  "Connected to PPU, no internet", "Offline, no signal", "Unsure",
];

// Area guess from the current route, so the menu starts on the right answer and
// most people never have to open it.
function areaFromPath(path) {
  const p = (path || "").toLowerCase();
  const hit = AREA_ROUTES.find(([fragment]) => p.includes(fragment));
  return hit ? hit[1] : "";
}

async function toAttachment(file) {
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
    const c = document.createElement("canvas");
    c.width = Math.max(1, Math.round(bmp.width * scale));
    c.height = Math.max(1, Math.round(bmp.height * scale));
    c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
    const dataUrl = c.toDataURL("image/jpeg", 0.8);
    return { filename: (file.name || "screenshot").replace(/\.[^.]+$/, "") + ".jpg", contentBase64: dataUrl.split(",")[1], contentType: "image/jpeg" };
  } catch {
    const buf = await file.arrayBuffer();
    const bytes = new Uint8Array(buf);
    let bin = "";
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return { filename: file.name || "screenshot", contentBase64: btoa(bin), contentType: file.type || "image/jpeg" };
  }
}

const appVersion = () => (typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "dev");

export default function FeedbackWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("issue"); // "issue" | "idea" (feature request)
  const [area, setArea] = useState("");
  const [connection, setConnection] = useState("");
  const [summary, setSummary] = useState("");
  const [steps, setSteps] = useState("");
  const [shots, setShots] = useState([]); // File[] — images / PDFs, emailed inline
  const [vids, setVids] = useState([]);  // File[] — videos, uploaded to storage, emailed as links
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  // Opened from the "Send feedback" item in the profile menu (TopNav). No
  // floating button — Jill 2026-07-17: the pill was in the way on some screens.
  useEffect(() => {
    const onOpen = () => {
      setType("issue");
      setArea(areaFromPath(location.pathname));
      setConnection(""); setSummary(""); setSteps(""); setShots([]); setVids([]); setDone(false); setErr("");
      setOpen(true);
    };
    window.addEventListener("seapa:open-feedback", onOpen);
    return () => window.removeEventListener("seapa:open-feedback", onOpen);
  }, [location.pathname]);

  if (!user) return null;

  async function submit() {
    setSending(true); setErr("");
    try {
      const attachments = [];
      for (const f of shots.slice(0, 6)) attachments.push(await toAttachment(f));
      // Videos go straight to storage — far too big for the JSON body or an
      // email attachment. Only the path rides along; the server signs a
      // 30-day link into the email. Generous deadline: a screen recording
      // over ship wifi is exactly the case this exists for.
      const videos = [];
      for (const v of vids.slice(0, 2)) {
        const safe = (v.name || "video").replace(/[^\w.-]+/g, "_");
        const path = `${user.id}/${Date.now()}-${safe}`;
        const { error: upErr } = await withTimeout(
          supabase.storage.from("feedback-videos").upload(path, v, { contentType: v.type || "video/mp4" }),
          300000, "The video upload didn't finish. Try a shorter clip, or remove it and send without."
        );
        if (upErr) throw new Error(`The video couldn't upload (${upErr.message}). Try again, or remove it and send without.`);
        videos.push({ path, filename: v.name || "video", size: v.size, contentType: v.type || "" });
      }
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      const body = {
        type, area, connection, summary, steps,
        page: `${location.pathname}${location.search || ""}`,
        app_version: appVersion(),
        from_auth_user_id: user.id,
        from_name: user.email || "",
        from_email: user.email || "",
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        screenshots: attachments,
        videos,
      };
      // The server now saves the report and emails Jill in the background, so
      // this call returns as soon as the report is stored — it no longer waits
      // on the email. The only thing left on the wire is uploading the
      // screenshot itself, so keep a generous deadline for ship wifi (2026-07-20).
      const { data, error } = await withTimeout(
        supabase.functions.invoke("submit_feedback", {
          body,
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }), 45000);
      if (error || !data?.ok) throw new Error(data?.error || error?.message || "Send failed");
      setDone(true);
    } catch (e) {
      setErr(e?.message || "Couldn't send. Check your connection and try again.");
    } finally {
      setSending(false);
    }
  }

  const label = { display: "block", fontSize: 12, fontWeight: 700, color: "var(--ink)", margin: "10px 0 3px" };
  const field = { width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid var(--field-border)", background: "var(--surface)", color: "var(--ink)", fontSize: 14, boxSizing: "border-box" };

  if (!open) return null;
  return (
    <>
      {open && (
        <div
          // Tapping the backdrop deliberately does NOT close the form: only
          // Send or Cancel do (Jill 2026-08-17, after Pat lost a whole typed
          // report to a stray tap outside the card on an iPhone).
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1200, display: "flex", justifyContent: "center", alignItems: "flex-start", padding: 16, overflowY: "auto" }}
        >
          <div
            style={{ background: "var(--surface)", color: "var(--ink)", border: "1px solid var(--border)", borderRadius: 10, padding: 18, width: 460, maxWidth: "100%", marginTop: "6vh" }}
          >
            {done ? (
              <>
                <h3 style={{ marginTop: 0 }}>Thank you</h3>
                <p style={{ color: "var(--text-muted)", lineHeight: 1.5 }}>
                  Your feedback went to the app developer. Nothing else to do.
                </p>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button onClick={() => setOpen(false)} style={{ ...field, width: "auto", cursor: "pointer", fontWeight: 700 }}>Close</button>
                </div>
              </>
            ) : (
              <>
                <h3 style={{ marginTop: 0 }}>Send feedback</h3>

                <div style={{ display: "flex", gap: 0, marginBottom: 10, border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
                  {[["issue", "Report an issue"], ["idea", "Feature request"]].map(([val, txt]) => (
                    <button key={val} onClick={() => setType(val)}
                      style={{ flex: 1, padding: "8px 10px", cursor: "pointer", border: "none",
                        background: type === val ? "var(--btn-primary-bg)" : "transparent",
                        color: type === val ? "var(--btn-primary-fg)" : "var(--ink)", fontWeight: 700, fontSize: "0.85rem" }}>
                      {txt}
                    </button>
                  ))}
                </div>

                <p style={{ color: "var(--text-muted)", fontSize: "0.82rem", margin: "0 0 4px", lineHeight: 1.5 }}>
                  {type === "issue"
                    ? "A couple of details make this much easier to fix. The app version and the page you're on are included automatically."
                    : "Tell us what would help. The app version and the page you're on are included automatically."}
                </p>

                <label style={label}>{type === "issue" ? "Which part of the portal?" : "Which part does this relate to?"}</label>
                <select style={field} value={area} onChange={(e) => setArea(e.target.value)}>
                  <option value="">Choose one</option>
                  {AREA_GROUPS.map((g) => (
                    <optgroup key={g.group} label={g.group}>
                      {g.areas.map((a) => <option key={a.name} value={a.name}>{a.name}</option>)}
                    </optgroup>
                  ))}
                </select>

                {type === "issue" && (
                  <>
                    <label style={label}>What connection were you on?</label>
                    <select style={field} value={connection} onChange={(e) => setConnection(e.target.value)}>
                      <option value="">Choose one</option>
                      {CONNECTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </>
                )}

                <label style={label}>{type === "issue" ? "Overview of issue" : "What would you like to add or change?"}</label>
                <textarea style={{ ...field, minHeight: 60, resize: "vertical" }} value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  placeholder={type === "issue"
                    ? "Example: I tapped Submit Final Slip and nothing happened."
                    : "Example: A one-tap button to copy last trip's times into a new slip."} />

                <label style={label}>{type === "issue" ? "What were you doing before issue, step by step?" : "How would it help? (optional)"}</label>
                <textarea style={{ ...field, minHeight: 60, resize: "vertical" }} value={steps}
                  onChange={(e) => setSteps(e.target.value)}
                  placeholder={type === "issue"
                    ? "Example: Opened the slip from My Schedule, filled the times, sent it via Second pilot handoff to PilotS."
                    : "Example: Saves re-typing the same times on back-to-back trips on the same ship."} />

                <label style={label}>Screenshots or a video (a picture or screen recording saves a lot of back-and-forth)</label>
                {/* Screenshots stay IMAGES on purpose (Jill 2026-07-26): they are
                    emailed to Jill and an image previews inline in the mail,
                    where a PDF becomes an attachment she has to open. So unlike
                    the certificate and receipt spots, nothing is converted here.
                    PDFs are still accepted because the occasional piece of
                    feedback is about a piece of paper, and toAttachment() already
                    passes a non-image through untouched. Videos (Jake Baken
                    2026-08-20) never ride the email: they upload to the
                    feedback-videos bucket in submit() and Jill gets a link. */}
                <input type="file" accept={`${PHOTO_ACCEPT},${PDF_ACCEPT},video/*`} multiple
                  onChange={(e) => {
                    const files = [...(e.target.files || [])];
                    const isVideo = (f) => (f.type || "").startsWith("video/");
                    const tooBig = files.filter((f) => isVideo(f) && f.size > 200 * 1024 * 1024);
                    setErr(tooBig.length ? `${tooBig[0].name} is over the 200 MB video limit. A shorter clip or a screen recording of just the problem works best.` : "");
                    setShots(files.filter((f) => !isVideo(f)));
                    setVids(files.filter((f) => isVideo(f) && f.size <= 200 * 1024 * 1024).slice(0, 2));
                  }} style={{ fontSize: 13 }} />
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>Videos up to 200 MB each (2 max). On an iPhone, a screen recording of the problem is perfect.</div>
                {(shots.length > 0 || vids.length > 0) && (
                  <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>
                    {[shots.length ? `${shots.length} picture${shots.length > 1 ? "s" : ""}` : "", vids.length ? `${vids.length} video${vids.length > 1 ? "s" : ""}` : ""].filter(Boolean).join(" + ")} attached
                  </div>
                )}

                {err && <div style={{ color: "var(--danger-fg)", fontSize: "0.82rem", marginTop: 10 }}>{err}</div>}

                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
                  <button onClick={() => setOpen(false)} disabled={sending} style={{ ...field, width: "auto", cursor: "pointer", fontWeight: 700, color: "var(--primary)" }}>Cancel</button>
                  <button
                    onClick={submit}
                    disabled={sending || (!summary.trim() && !steps.trim() && shots.length === 0 && vids.length === 0)}
                    style={{ ...field, width: "auto", cursor: "pointer", fontWeight: 700, border: "none", background: "var(--btn-primary-bg)", color: "var(--btn-primary-fg)", opacity: (sending || (!summary.trim() && !steps.trim() && shots.length === 0 && vids.length === 0)) ? 0.6 : 1 }}
                  >
                    {sending ? (vids.length ? "Sending… (video uploading, hang tight)" : "Sending…") : "Send"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
